import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ArrowDown, FileText, Users, DollarSign, Loader2 } from "lucide-react";
import { prettyFieldLabel } from "../utils/labels";
import { fmtCurrency } from "../utils/format";

interface TraceContributor {
  tenant_name?: string;
  category?: string;
  leased_sf?: number;
  annual_contribution?: number;
  amount?: number;
  field_paths: string[];
}

interface TraceData {
  line: string;
  year: number;
  calculation: string;
  contributors: TraceContributor[];
  source_documents: Array<{ doc_id: string; filename: string; file_type: string }>;
}

interface SourceTracePopoverProps {
  dealId: string;
  lineLabel: string;
  value: string;
  year: number;
  onClose: () => void;
}

export default function SourceTracePopover({
  dealId,
  lineLabel,
  value,
  year,
  onClose,
}: SourceTracePopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<TraceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    import("../api/client").then(({ default: api }) => {
      api.get(`/api/deals/${dealId}/cash-flow-trace`, {
        params: { line: lineLabel, year },
      }).then((res) => {
        if (!cancelled) { setData(res.data); setLoading(false); }
      }).catch(() => {
        if (!cancelled) setLoading(false);
      });
    });

    return () => { cancelled = true; };
  }, [dealId, lineLabel, year]);

  return (
    <div
      ref={ref}
      className="absolute z-50 top-full right-0 mt-1 w-80 bg-white border border-grove-300 rounded-lg shadow-xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-grove-50 border-b border-grove-200">
        <h4 className="text-xs font-semibold text-bark-700 uppercase tracking-wider">
          Source Trace
        </h4>
        <button onClick={onClose} className="p-0.5 rounded text-bark-400 hover:text-bark-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-forest-600" />
          </div>
        ) : data ? (
          <div className="p-4 space-y-3">
            {/* Line item */}
            <div>
              <div className="text-[10px] font-medium text-bark-400 uppercase tracking-wider">Cash Flow Line</div>
              <div className="text-sm font-medium text-bark-700">{data.line}: {value}</div>
              <div className="text-[10px] text-bark-400">Year {data.year}</div>
            </div>

            <ArrowDown className="w-3 h-3 text-bark-300 mx-auto" />

            {/* Engine calculation */}
            <div>
              <div className="text-[10px] font-medium text-bark-400 uppercase tracking-wider">Engine Calculation</div>
              <div className="text-xs text-bark-600 leading-relaxed">{data.calculation}</div>
            </div>

            <ArrowDown className="w-3 h-3 text-bark-300 mx-auto" />

            {/* Contributors */}
            {data.contributors.length > 0 && (
              <div>
                <div className="text-[10px] font-medium text-bark-400 uppercase tracking-wider mb-1.5">
                  Contributing {data.contributors[0].tenant_name ? "Tenants" : "Items"}
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {data.contributors.slice(0, 10).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-bark-600 truncate">
                        {c.tenant_name ? (
                          <span className="inline-flex items-center gap-1">
                            <Users className="w-3 h-3 text-bark-400" />
                            {c.tenant_name}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <DollarSign className="w-3 h-3 text-bark-400" />
                            {c.category}
                          </span>
                        )}
                      </span>
                      <span className="tabular-nums text-bark-700 font-medium ml-2 whitespace-nowrap">
                        {c.annual_contribution != null
                          ? fmtCurrency(c.annual_contribution)
                          : c.amount != null
                          ? fmtCurrency(c.amount)
                          : ""}
                      </span>
                    </div>
                  ))}
                  {data.contributors.length > 10 && (
                    <div className="text-[10px] text-bark-400">
                      +{data.contributors.length - 10} more
                    </div>
                  )}
                </div>
              </div>
            )}

            <ArrowDown className="w-3 h-3 text-bark-300 mx-auto" />

            {/* Source documents */}
            <div>
              <div className="text-[10px] font-medium text-bark-400 uppercase tracking-wider mb-1.5">
                Source Documents
              </div>
              {data.source_documents.map((doc) => (
                <Link
                  key={doc.doc_id}
                  to={`/deals/${dealId}/verify?doc=${doc.doc_id}`}
                  className="flex items-center gap-1.5 text-xs text-forest-600 hover:text-forest-700 underline underline-offset-2 py-0.5"
                >
                  <FileText className="w-3 h-3" />
                  {doc.filename}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-bark-400">
            Could not load trace data
          </div>
        )}
      </div>
    </div>
  );
}
